// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-unused-expressions, @typescript-eslint/require-await, react-hooks/exhaustive-deps, react-hooks/immutability */

import React, { FC, useEffect, useState } from "react";
import { Segment, Table, Checkbox, Grid, Loader } from "semantic-ui-react";

import * as tableShader from "../../../Lumber/TableShader";
import * as valuePrecisionConverter from "../../../Lumber/ValuePrecisionConverter";

import _ from "lodash";
import { std } from "mathjs";
import { useProtocolAnalysis } from "./ProtocolAnalysisContext";

import type { ProtocolSiteSpecificTableProps, SiteSpecificTableRow, SiteTukeyLetter } from "./types";
import sharedStyles from "./ProtocolAnalysis.module.css";

/**
 * ValuePrecisionConverter is untyped JS (getPrecision infers as returning `any`). It returns falsy
 * input unchanged and rounds anything else to a number.
 */
const getPrecision = valuePrecisionConverter.getPrecision as {
	(value: number): number;
	(value: number | string | null | undefined): number | "" | null | undefined;
};

/** A site row that made it past the `average !== null` filter. */
type AveragedSiteRow = SiteSpecificTableRow & { average: number };

/** One `meanData` entry per trial, built by calculateMeanAndStandardDeviation. */
type SiteMeanRow = {
	/** "" when every value was null. */
	average: string;
	/** "" when the mean is NaN or every value was null. */
	standardDeviation: string;
	trialId: string;
	/** Only set when tukeyData is not empty. */
	pValue?: string | null;
	cv?: string | null;
	mean?: string;
};

const ProtocolSiteSpecificTable: FC<ProtocolSiteSpecificTableProps> = ({
	siteSpecificData,
	updateCheckedTrial,
	appendedPercent,
	disableCheckBoxes,
	tukeyData,
	tukeyDataLoading,
	hideMeanComparisonResults,
	needToRerunNormalization,
	needToRerunStats,
	allMeanComparisonData,
	uniqueAnalysesCount,
	hideCheckAll,
	collapseView = false,
}) => {
	// TODO(types): allMeanComparisonData and uniqueAnalysesCount are never passed by any parent. The
	// checkbox `disabled`/onClick checks below only work because undefined === undefined.
	const {
		pending: { selectedTrialIds },
	} = useProtocolAnalysis();

	//-- Data Sources
	const [tableHeaderData, setTableHeaderData] = useState<AveragedSiteRow[] | null>(null);
	const [tableData, setTableData] = useState<_.Dictionary<AveragedSiteRow[]> | null>(null);
	const [meanData, setMeanData] = useState<SiteMeanRow[] | null>(null);
	const [singleSiteTukeyLoading, setSingleSiteTukeyLoading] = useState(false);
	const [previousTukeyData, setPreviousTukeyData] = useState<SiteTukeyLetter[] | null>(null);
	const [toggleAll, setToggleAll] = useState(true);

	//A tick restamps siteSpecificData, so the letters are merged back in rather than dropped with the old rows
	useEffect(() => {
		if (siteSpecificData) {
			updateTableData();
		}
	}, [siteSpecificData]);

	useEffect(() => {
		//-- We want to run this even when length is 0 to clear site specific table cld

		if (tukeyData && tukeyData.length > 0 && !_.isEqual(tukeyData, previousTukeyData)) {
			setSingleSiteTukeyLoading((!tukeyData || tukeyData?.length === 0) && !hideMeanComparisonResults);
			updateTableData();
			setPreviousTukeyData(tukeyData);
		}
	}, [tukeyData]);

	async function updateCheckAll() {
		const toggleValue = toggleAll;
		setToggleAll(!toggleValue);
		return;
	}

	function toggleCheckAll() {
		const tempTableData = _.cloneDeep(tableHeaderData);
		// TODO(types): tableHeaderData is null until siteSpecificData arrives, but the Check All
		// checkbox already renders, so clicking it then throws here.
		tempTableData!.map((trial) => {
			trial.trialChecked = !toggleAll;
		});
		updateCheckedTrial(!toggleAll, _.map(tempTableData, "trialId"));
		setTableHeaderData(tempTableData);
	}

	function getTableData() {
		const orderedData = _.filter(
			_.sortBy(siteSpecificData, ["country", "region", "city", "trialName", "cooperatorName", "trialId"]),
			(d): d is AveragedSiteRow => d.average !== null,
		);
		const orderedHeaderData = _.uniqBy(orderedData, "trialId");
		setTableHeaderData(orderedHeaderData);
		const groupedTrials = _.groupBy(
			_.filter(orderedData, (d) => _.some<SiteSpecificTableRow["plots"][number]>(d.plots, (p) => !!p.plotId)),
			"trialId",
		);
		const averages = calculateMeanAndStandardDeviation(groupedTrials);
		Object.keys(groupedTrials).length > 0 &&
		groupedTrials[Object.keys(groupedTrials)[0]][0]?.assessmentName?.includes("MG50")
			? tableShader.calculateShadingForGroupedTreatmentReversed(groupedTrials, "average", "87, 184, 120")
			: tableShader.calculateShadingForGroupedTreatment(groupedTrials, "average", "87, 184, 120");
		const groupedData = _.groupBy(orderedData, "trialTreatmentId");

		if (!_.isEmpty(tukeyData)) {
			_.map(averages, (umd) => {
				const meanComparisonValue = _.find(tukeyData, (td) => {
					return td.trialId === umd.trialId;
				});

				umd.pValue = meanComparisonValue?.pValue;
				umd.cv = meanComparisonValue?.cv;
				// TODO(types): getPrecision returns "" unchanged for an empty-string mean, and "".toFixed is
				// undefined, so this would throw. The cast hides that case.
				umd.mean = (getPrecision(meanComparisonValue?.mean) as number | null | undefined)?.toFixed(2);
			});
		}

		setMeanData(averages);
		return groupedData;
	}

	function calculateMeanAndStandardDeviation(groupedTrials: _.Dictionary<AveragedSiteRow[]>) {
		const averages: SiteMeanRow[] = [];
		_.map(groupedTrials, (gt, i) => {
			const filtered = _.filter(gt, (t) => {
				return t.treatmentChecked;
			});
			const values = _.map(filtered, "average");

			//Use this to filter out null average data. This will update the protocol normalization and box whisker charts properly
			const nonNullValues = _.filter(values, (v) => {
				return v !== null;
			});
			const areAllValsNull = nonNullValues.length === 0;
			const mean = _.meanBy(_.filter(values, (v) => v !== null));
			const standardDeviation =
				isNaN(mean) || areAllValsNull ? "" : (std(_.filter(values, (v) => v !== null)) / mean) * 100;
			const average = {
				// TODO(types): only areAllValsNull is tested; `mean === isNaN(mean)` would compare a number to
				// a boolean and never match.
				average: areAllValsNull ? "" : getPrecision(mean).toFixed(2),
				standardDeviation:
					// standardDeviation is only "" under this same condition, so it is a number here.
					isNaN(mean) || areAllValsNull ? "" : getPrecision(standardDeviation as number).toFixed(2),
				trialId: i,
			};
			averages.push(average);
		});

		return averages;
	}

	function updateTableData() {
		const updatedTableData = getTableData();

		_.map(updatedTableData, (utd, trialTreatmentId) => {
			_.map(utd, (trialData) => {
				const tukeyLetters = _.find(
					tukeyData,
					(td) =>
						td.trialId === trialData.trialId &&
						Number(td.trialTreatmentId) === Number(trialTreatmentId) &&
						trialData.treatmentChecked,
				)?.tukeyLetters;

				trialData.tukeyLetters = tukeyLetters;
			});
		});

		//-- Recreates table to look at single trial
		//---- If all letters per trial are 'a', set them to '-'
		if (updatedTableData) {
			//Read from this run's rows: the tableHeaderData getTableData just set is not readable until the next render
			_.map(_.uniqBy(_.flatten(_.values(updatedTableData)), "trialId"), (thd) => {
				const trialData = _.map(updatedTableData, (utd) => {
					const td = _.find(utd, (trialData) => {
						return trialData.trialId === thd.trialId;
					});
					return td;
				});

				const checkedTrialData = _.filter(trialData, (td): td is AveragedSiteRow => {
					return !!td && td.treatmentChecked && td.average !== null && !td.excluded;
				});

				const allLettersAreA = _.every(checkedTrialData, ["tukeyLetters", "a"]);

				if (allLettersAreA) {
					_.map(checkedTrialData, (td) => {
						td.tukeyLetters = "-";
					});
				}
			});
		}

		setTableData(updatedTableData);
	}

	return (
		<Segment basic style={{ width: "fit-content" }}>
			<Table celled fixed striped selectable color="blue" textAlign="center" style={{ width: "fit-content" }}>
				<Table.Header>
					<Table.Row>
						{hideCheckAll ? (
							<Table.HeaderCell className={sharedStyles.rowTitle} style={{ textAlign: "center" }} />
						) : (
							<Table.HeaderCell className={sharedStyles.rowTitle} style={{ textAlign: "center" }}>
								<Checkbox
									label="Check All"
									checked={toggleAll}
									disabled={disableCheckBoxes || allMeanComparisonData?.length !== uniqueAnalysesCount?.length}
									onClick={() => {
										if (!disableCheckBoxes && allMeanComparisonData?.length === uniqueAnalysesCount?.length) {
											// TODO(types): toggleCheckAll runs as soon as updateCheckAll() starts (with the old
											// toggleAll), not when it settles.
											void updateCheckAll();
											toggleCheckAll();
										}
									}}
								/>
							</Table.HeaderCell>
						)}
						{_.reduce<AveragedSiteRow, React.ReactNode[]>(
							tableHeaderData,
							(acc, thd) => {
								const isChecked = selectedTrialIds ? selectedTrialIds.includes(thd.trialId) : true;

								if (!collapseView || isChecked) {
									acc.push(
										<Table.HeaderCell key={"checkbox-" + thd.trialId} style={{ width: 152 }}>
											<Checkbox
												checked={isChecked}
												disabled={disableCheckBoxes || allMeanComparisonData?.length !== uniqueAnalysesCount?.length}
												onChange={() => {
													updateCheckedTrial(!isChecked, thd.trialId);
												}}
											/>
										</Table.HeaderCell>,
									);
								}
								return acc;
							},
							[],
						)}
					</Table.Row>
					<Table.Row>
						<Table.HeaderCell className={sharedStyles.rowTitle}>Trial Name</Table.HeaderCell>
						{_.reduce<AveragedSiteRow, React.ReactNode[]>(
							tableHeaderData,
							(acc, thd) => {
								if (!collapseView || thd.trialChecked) {
									acc.push(
										<Table.HeaderCell key={"name-" + thd.trialId} style={{ fontWeight: "400", background: "white" }}>
											{/* TODO(types): trialName is nullable, and replaceAll on null throws. */}
											<span dangerouslySetInnerHTML={{ __html: thd.trialName!.replaceAll("_", "_<wbr/>") }} />
										</Table.HeaderCell>,
									);
								}
								return acc;
							},
							[],
						)}
					</Table.Row>
					<Table.Row>
						<Table.HeaderCell className={sharedStyles.rowTitle}>Cooperator Name</Table.HeaderCell>
						{_.reduce<AveragedSiteRow, React.ReactNode[]>(
							tableHeaderData,
							(acc, thd) => {
								if (!collapseView || thd.trialChecked) {
									acc.push(
										<Table.HeaderCell
											key={"coop-name-" + thd.trialId}
											style={{ fontWeight: "400", background: "white" }}
										>
											{thd.cooperatorName}
										</Table.HeaderCell>,
									);
								}
								return acc;
							},
							[],
						)}
					</Table.Row>
					<Table.Row>
						<Table.HeaderCell className={sharedStyles.rowTitle}>Farm Location</Table.HeaderCell>
						{_.reduce<AveragedSiteRow, React.ReactNode[]>(
							tableHeaderData,
							(acc, thd) => {
								if (!collapseView || thd.trialChecked) {
									acc.push(
										<Table.HeaderCell key={"region-" + thd.trialId} style={{ fontWeight: "400", background: "white" }}>
											{thd.city}, {thd.region}, {thd.countryCode}
										</Table.HeaderCell>,
									);
								}
								return acc;
							},
							[],
						)}
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{_.reduce<_.Dictionary<AveragedSiteRow[]>, React.ReactNode[]>(
						tableData,
						(acc, td) => {
							if (!collapseView || td[0].treatmentChecked) {
								acc.push(
									<Table.Row key={td[0]?.trialTreatmentId}>
										<Table.Cell className={sharedStyles.rowTitle}>{td[0]?.trialTreatmentId}</Table.Cell>
										{_.reduce<AveragedSiteRow, React.ReactNode[]>(
											tableHeaderData,
											(acc, thd) => {
												const treatment = _.find(td, { trialId: thd.trialId });
												if (!collapseView || treatment?.trialChecked) {
													acc.push(
														treatment ? (
															<Table.Cell
																key={thd.trialId}
																style={{
																	color: needToRerunNormalization
																		? ""
																		: treatment.treatmentChecked && treatment.trialChecked
																			? treatment.textColor
																			: "black",
																	backgroundColor: needToRerunNormalization
																		? ""
																		: treatment.treatmentChecked && treatment.trialChecked
																			? treatment.color
																			: "lightGray",
																	width: "150px",
																}}
															>
																<Grid style={{ width: "inherit", height: "inherit" }}>
																	<Grid.Row style={{ padding: "inherit" }}>
																		<Grid.Column width="1" />
																		<Grid.Column width="9" style={{ padding: 0 }}>
																			{!treatment.treatmentChecked || needToRerunNormalization
																				? ""
																				: treatment.excluded === true
																					? "-"
																					: getPrecision(treatment.average)}
																			{!treatment.treatmentChecked || needToRerunNormalization
																				? ""
																				: appendedPercent && treatment.excluded === false
																					? "%"
																					: ""}
																		</Grid.Column>
																		{
																			<Grid.Column width="3" style={{ padding: 0 }}>
																				{tukeyDataLoading || disableCheckBoxes || singleSiteTukeyLoading ? (
																					<Loader active size="tiny" style={{ zIndex: "0" }} />
																				) : treatment.excluded === true ||
																				  !treatment.treatmentChecked ||
																				  needToRerunNormalization ||
																				  !treatment.trialChecked ? (
																					""
																				) : hideMeanComparisonResults || needToRerunStats ? null : (
																					treatment.tukeyLetters
																				)}
																			</Grid.Column>
																		}
																	</Grid.Row>
																</Grid>
															</Table.Cell>
														) : (
															<Table.Cell key={thd.trialId} style={{ width: "150px" }}>
																{"-"}
															</Table.Cell>
														),
													);
												}
												return acc;
											},
											[],
										)}
									</Table.Row>,
								);
							}
							return acc;
						},
						[],
					)}
					<Table.Row>
						<Table.Cell className={sharedStyles.rowTitle}>CV</Table.Cell>
						{_.reduce<SiteMeanRow, React.ReactNode[]>(
							meanData,
							(acc, md) => {
								const isChecked = selectedTrialIds ? selectedTrialIds.includes(md.trialId) : true;

								if (!collapseView || isChecked) {
									acc.push(
										needToRerunNormalization ||
											hideMeanComparisonResults ||
											tukeyDataLoading ||
											disableCheckBoxes ||
											singleSiteTukeyLoading ? (
											<Table.Cell key={"cv-" + md.trialId}></Table.Cell>
										) : (
											<Table.Cell key={"cv-" + md.trialId}>
												{Math.abs(Number(getPrecision(md?.cv))).toFixed(2)}%
											</Table.Cell>
										),
									);
								}
								return acc;
							},
							[],
						)}
					</Table.Row>
					{
						<Table.Row>
							<Table.Cell className={sharedStyles.rowTitle}>P-Value</Table.Cell>
							{_.reduce<SiteMeanRow, React.ReactNode[]>(
								meanData,
								(acc, md) => {
									const isChecked = selectedTrialIds ? selectedTrialIds.includes(md.trialId) : true;

									if (!collapseView || isChecked) {
										acc.push(
											<Table.Cell key={"pv-" + md.trialId}>
												{!hideMeanComparisonResults &&
												!needToRerunNormalization &&
												!needToRerunStats &&
												!tukeyDataLoading &&
												!disableCheckBoxes &&
												!singleSiteTukeyLoading
													? Number(Number(md?.pValue).toFixed(5)) === 0
														? "<0.00001"
														: Number(Number(md?.pValue).toFixed(5))
													: null}
											</Table.Cell>,
										);
									}
									return acc;
								},
								[],
							)}
						</Table.Row>
					}
				</Table.Body>
			</Table>
		</Segment>
	);
};

export default ProtocolSiteSpecificTable;
